import { createAsyncThunk } from "@reduxjs/toolkit";
import dayjs from "dayjs";

import TOAST from "../../utils/constants/toast";
import { AgencyAPI } from "../services/AgencyService";
import { PosAPI } from "../services/PosService";
import { SessionApi } from "../services/SessionService";
import {
  updateAgency,
  updateAgent,
  updateAgentName,
  updateSessionKey,
} from "../slices/sessionSlice";
import { showToast } from "../slices/toastSlice";
import { RootState } from "../store";

import { getAgencyDetails } from "./agencyActions";

const DEV_API_USERNAME = process.env.REACT_APP_DEV_API_USERNAME as string;
const DEV_API_PASSWORD = process.env.REACT_APP_DEV_API_PASSWORD as string;

type SwitchAgentSessionPayload = {
  agencyId: string;
  agentId: string;
  agentName?: string;
};

export const initApiToken = createAsyncThunk(
  "session/initApiToken",
  async (_, { dispatch }) => {
    const { data } = await dispatch(
      SessionApi.endpoints.initApiToken.initiate({
        username: DEV_API_USERNAME,
        password: DEV_API_PASSWORD,
      }),
    );

    if (!data) {
      dispatch(
        showToast({
          type: TOAST.ERROR_TYPE,
          message: "Wrong credentials",
          duration: TOAST.DEFAULT_DURATION,
        }),
      );
    }

    return data;
  },
);

export const initPosSession = createAsyncThunk(
  "session/initPosSession",
  async ({ silent }: { silent?: boolean } = {}, { getState, dispatch }) => {
    const { agent, agency } = (getState() as RootState).session;
    const { data } = await dispatch(
      PosAPI.endpoints.initPosSession.initiate({ agent, agency }),
    );

    if (!data) {
      dispatch(
        showToast({
          type: TOAST.ERROR_TYPE,
          message: "Sign in failed",
          duration: TOAST.DEFAULT_DURATION,
        }),
      );
    } else if (data && !silent) {
      dispatch(
        showToast({
          type: TOAST.SUCCESS_TYPE,
          message: "Successfully signed in",
          duration: TOAST.DEFAULT_DURATION,
        }),
      );
    }

    return data;
  },
);

export const getAgentById = createAsyncThunk(
  "session/getAgentById",
  async (agentId: string, { dispatch }) => {
    const { data } = await dispatch(
      AgencyAPI.endpoints.getAgentDetails.initiate(agentId),
    );

    return data;
  },
);

export const destroySession = createAsyncThunk("session/destroySession", () => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
  }

  localStorage.clear();
  sessionStorage.clear();
});

const getCookie = (name: string): string => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));

  return match ? match[2] : "";
};

const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${value}; secure; path=/`;
};

const deleteCookie = (name: string) => {
  document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
};

export const switchAgentSession = createAsyncThunk(
  "session/switchAgentSession",
  async (
    { agencyId, agentId, agentName }: SwitchAgentSessionPayload,
    { dispatch },
  ) => {
    dispatch(updateAgency(agencyId));
    dispatch(updateAgent(agentId));

    if (agentName) {
      dispatch(updateAgentName(agentName));
    }

    setCookie("active_agency", agencyId);
    setCookie("active_agent", agentId);

    if (agentName) {
      setCookie("active_agentName", agentName);
    }

    const pos = await dispatch(initPosSession({ silent: true })).unwrap();

    if (!pos?.key) {
      dispatch(
        showToast({
          type: TOAST.ERROR_TYPE,
          message: "Switch session failed",
          duration: TOAST.DEFAULT_DURATION,
        }),
      );

      deleteCookie("active_agency");
      deleteCookie("active_agent");
      deleteCookie("active_agentName");

      return null;
    }

    dispatch(updateSessionKey(pos.key));
    setCookie("active_sessionKey", pos.key);
    setCookie("active_sessionKey_exp", dayjs().add(3600, "seconds").format());

    await dispatch(getAgencyDetails(agencyId));
    await dispatch(getAgentById(agentId));

    dispatch(
      showToast({
        type: TOAST.SUCCESS_TYPE,
        message: "Session switched",
        duration: TOAST.DEFAULT_DURATION,
      }),
    );

    return pos;
  },
);

export const revertToOriginalSession = createAsyncThunk(
  "session/revertToOriginalSession",
  async (_, { dispatch }) => {
    const originalAgency = getCookie("agency");
    const originalAgent = getCookie("agent");
    const originalAgentName = getCookie("agentName");
    const originalSessionKey = getCookie("sessionKey");

    if (!originalSessionKey || !originalAgency || !originalAgent) {
      dispatch(destroySession());

      return;
    }

    deleteCookie("active_sessionKey");
    deleteCookie("active_sessionKey_exp");
    deleteCookie("active_agency");
    deleteCookie("active_agent");
    deleteCookie("active_agentName");

    dispatch(updateAgency(originalAgency));
    dispatch(updateAgent(originalAgent));
    dispatch(updateAgentName(originalAgentName));
    dispatch(updateSessionKey(originalSessionKey));

    await dispatch(getAgencyDetails(originalAgency));
    await dispatch(getAgentById(originalAgent));

    dispatch(
      showToast({
        type: TOAST.SUCCESS_TYPE,
        message: "Returned to original session",
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  },
);
